import { PlusOutlined } from '@ant-design/icons';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Form } from 'antd';
import { useState } from 'react';

import { SList } from 'app/components/StaycationUI/List';
import type { RoomType } from 'app/components/commons/Package/General/Types';

import { RoomItem } from './RoomItem/RoomItem';
import './RoomList.scss';
import { RoomModal } from './RoomModal/RoomModal';

export const RoomList = ({ mode, rooms, allRooms, move }: Props) => {
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);

  const isEmpty = !rooms || !rooms.length;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  return (
    <div className="list-rooms">
      <Form.List name="rooms">
        {(fields, { add, remove }) => {
          const onSelect = (values: RoomType[]) =>
            values
              .filter((room) => !rooms?.some((e) => e.id === room.id))
              .forEach((room) => add({ ...room, included: null }));

          const items = mode === 'edit' || !isEmpty ? fields : [];
          const itemIds = items.map(
            (field, index) => rooms?.[index]?.id ?? field.key
          );

          const handleDragEnd = ({ active, over }: DragEndEvent) => {
            if (!over || active.id === over.id) {
              return;
            }

            const oldIndex = itemIds.findIndex((id) => id === active.id);
            const newIndex = itemIds.findIndex((id) => id === over.id);

            if (oldIndex === -1 || newIndex === -1) {
              return;
            }

            move({ oldIndex, newIndex });
          };

          return (
            <>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={itemIds}
                  strategy={verticalListSortingStrategy}
                >
                  <SList
                    data={items}
                    renderItem={(field, index) => (
                      <RoomItem
                        field={field}
                        dndId={itemIds[index]}
                        room={rooms?.[index]}
                        mode={mode}
                        remove={() => remove(index)}
                        key={itemIds[index]}
                        disabled={mode === 'view'}
                      />
                    )}
                    bordered
                  />
                </SortableContext>
              </DndContext>
              {mode === 'edit' && (
                <div
                  onClick={() => setIsSelectorOpen(true)}
                  className="list-rooms__add-button"
                >
                  <PlusOutlined />
                  <span className="text">{`Add rooms`}</span>
                </div>
              )}
              <RoomModal
                visible={isSelectorOpen}
                onSelect={onSelect}
                onCancel={() => setIsSelectorOpen(false)}
                allRooms={allRooms}
              />
            </>
          );
        }}
      </Form.List>
    </div>
  );
};

export const SortableRoomList = (props: SortableProps) => (
  <RoomList {...props} />
);

type Props = {
  mode: string;
  rooms?: RoomType[];
  allRooms?: RoomType[];
  move: (indexes: { oldIndex: number; newIndex: number }) => void;
};

type SortableProps = Props;
